import type { CSSProperties } from 'vue'
import type { ThemeId, WebsiteTheme, WebsiteThemeTokens } from './types'

export const themes = [
  {
    id: 'harbor-blue',
    name: 'Harbor Blue',
    description: 'Clear, dependable blues for established local professionals.',
    tokens: {
      colors: {
        background: 'oklch(0.98 0.01 245)',
        surface: 'oklch(1 0 0)',
        text: 'oklch(0.22 0.04 252)',
        mutedText: 'oklch(0.48 0.04 252)',
        primary: 'oklch(0.5 0.18 252)',
        primaryText: 'oklch(0.99 0 0)',
        accent: 'oklch(0.91 0.06 225)',
        border: 'oklch(0.89 0.025 245)',
      },
      actions: {
        primary: {
          background: 'oklch(0.5 0.18 252)',
          text: 'oklch(0.99 0 0)',
          border: 'oklch(0.5 0.18 252)',
        },
        secondary: {
          background: 'oklch(1 0 0)',
          text: 'oklch(0.22 0.04 252)',
          border: 'oklch(0.89 0.025 245)',
        },
        tertiary: {
          background: 'transparent',
          text: 'oklch(0.5 0.18 252)',
          border: 'transparent',
        },
      },
      typography: {
        heading: 'Inter, ui-sans-serif, system-ui, sans-serif',
        body: 'Inter, ui-sans-serif, system-ui, sans-serif',
      },
      shape: { smallRadius: '0.5rem', mediumRadius: '0.8rem', largeRadius: '1.25rem' },
      shadows: {
        small: '0 1px 3px oklch(0.22 0.04 252 / 0.08)',
        medium: '0 16px 40px oklch(0.22 0.04 252 / 0.12)',
      },
      layout: { contentWidth: '72rem' },
    },
  },
  {
    id: 'warm-terracotta',
    name: 'Warm Terracotta',
    description: 'Welcoming earth tones with a handcrafted, personal character.',
    tokens: {
      colors: {
        background: 'oklch(0.97 0.025 65)',
        surface: 'oklch(0.995 0.008 70)',
        text: 'oklch(0.25 0.04 42)',
        mutedText: 'oklch(0.5 0.045 45)',
        primary: 'oklch(0.55 0.16 38)',
        primaryText: 'oklch(0.98 0.01 65)',
        accent: 'oklch(0.88 0.08 72)',
        border: 'oklch(0.85 0.045 60)',
      },
      actions: {
        primary: {
          background: 'oklch(0.55 0.16 38)',
          text: 'oklch(0.98 0.01 65)',
          border: 'oklch(0.55 0.16 38)',
        },
        secondary: {
          background: 'oklch(0.995 0.008 70)',
          text: 'oklch(0.25 0.04 42)',
          border: 'oklch(0.85 0.045 60)',
        },
        tertiary: {
          background: 'transparent',
          text: 'oklch(0.55 0.16 38)',
          border: 'transparent',
        },
      },
      typography: {
        heading: 'Georgia, Cambria, Times New Roman, serif',
        body: 'Inter, ui-sans-serif, system-ui, sans-serif',
      },
      shape: { smallRadius: '0.25rem', mediumRadius: '0.5rem', largeRadius: '0.75rem' },
      shadows: {
        small: '0 2px 5px oklch(0.25 0.04 42 / 0.08)',
        medium: '0 18px 45px oklch(0.25 0.04 42 / 0.14)',
      },
      layout: { contentWidth: '70rem' },
    },
  },
  {
    id: 'evergreen-studio',
    name: 'Evergreen Studio',
    description: 'Confident greens and soft geometry for modern service brands.',
    tokens: {
      colors: {
        background: 'oklch(0.97 0.018 150)',
        surface: 'oklch(0.995 0.006 150)',
        text: 'oklch(0.2 0.045 155)',
        mutedText: 'oklch(0.46 0.04 155)',
        primary: 'oklch(0.43 0.12 154)',
        primaryText: 'oklch(0.99 0 0)',
        accent: 'oklch(0.88 0.08 140)',
        border: 'oklch(0.86 0.04 150)',
      },
      actions: {
        primary: {
          background: 'oklch(0.43 0.12 154)',
          text: 'oklch(0.99 0 0)',
          border: 'oklch(0.43 0.12 154)',
        },
        secondary: {
          background: 'oklch(0.995 0.006 150)',
          text: 'oklch(0.2 0.045 155)',
          border: 'oklch(0.86 0.04 150)',
        },
        tertiary: {
          background: 'transparent',
          text: 'oklch(0.43 0.12 154)',
          border: 'transparent',
        },
      },
      typography: {
        heading: 'Inter, ui-sans-serif, system-ui, sans-serif',
        body: 'Inter, ui-sans-serif, system-ui, sans-serif',
      },
      shape: { smallRadius: '0.75rem', mediumRadius: '1.15rem', largeRadius: '1.75rem' },
      shadows: {
        small: '0 2px 8px oklch(0.2 0.045 155 / 0.07)',
        medium: '0 20px 50px oklch(0.2 0.045 155 / 0.12)',
      },
      layout: { contentWidth: '74rem' },
    },
  },
] as const satisfies readonly WebsiteTheme[]

export function getTheme(themeId: ThemeId): WebsiteTheme {
  const theme = themes.find((candidate) => candidate.id === themeId)

  if (!theme) {
    throw new Error(`Unknown website theme: ${themeId}`)
  }

  return structuredClone(theme)
}

export function themeTokensToStyle(tokens: WebsiteThemeTokens): CSSProperties {
  return {
    '--site-color-background': tokens.colors.background,
    '--site-color-surface': tokens.colors.surface,
    '--site-color-text': tokens.colors.text,
    '--site-color-muted-text': tokens.colors.mutedText,
    '--site-color-primary': tokens.colors.primary,
    '--site-color-primary-text': tokens.colors.primaryText,
    '--site-color-accent': tokens.colors.accent,
    '--site-color-border': tokens.colors.border,
    '--site-action-primary-background': tokens.actions.primary.background,
    '--site-action-primary-text': tokens.actions.primary.text,
    '--site-action-primary-border': tokens.actions.primary.border,
    '--site-action-secondary-background': tokens.actions.secondary.background,
    '--site-action-secondary-text': tokens.actions.secondary.text,
    '--site-action-secondary-border': tokens.actions.secondary.border,
    '--site-action-tertiary-background': tokens.actions.tertiary.background,
    '--site-action-tertiary-text': tokens.actions.tertiary.text,
    '--site-action-tertiary-border': tokens.actions.tertiary.border,
    '--site-font-heading': tokens.typography.heading,
    '--site-font-body': tokens.typography.body,
    '--site-radius-sm': tokens.shape.smallRadius,
    '--site-radius-md': tokens.shape.mediumRadius,
    '--site-radius-lg': tokens.shape.largeRadius,
    '--site-shadow-sm': tokens.shadows.small,
    '--site-shadow-md': tokens.shadows.medium,
    '--site-content-width': tokens.layout.contentWidth,
  } as CSSProperties
}
